import React, { useState, useEffect, useReducer, useRef, useCallback, useContext } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import { getSocket } from "../../services/socket-io";
import useResyncOnResume from "../../hooks/useResyncOnResume";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  CircularProgress,
  IconButton,
  makeStyles,
  Tooltip,
  useTheme,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ErrorOutline,
  ExpandMore,
  FileCopy,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Reply as ReplyIcon,
} from "@material-ui/icons";
import { Chip, Tooltip as MuiTooltip } from "@material-ui/core";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import MatchPropAlertCard from "../MatchPropAlertCard";
import DocumentPreview from "../DocumentPreview";
import whatsBackground from "../../assets/wa-background.webp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { fmtDateTimeShort, fmtSmartTimestamp } from "../../translate/localeHelper";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import Audio from "../Audio";
import AudioTranscription from "../AudioTranscription";
import getParticipantColor from "../../helpers/getParticipantColor";
import { getMetaErrorMessage } from "../../helpers/metaErrorCodes";
import {
  isMediaFilename,
  isDocumentMediaType,
  looksLikeDocumentName,
  parseDocumentName,
  getDocumentExtension,
  getDocumentIcon,
} from "../../helpers/mediaPlaceholder";

const HISTORY_MEDIA_TYPES = ["image", "video", "audio", "ptt", "document", "sticker"];

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  // feat_264 — chip "↪ Reenviado" (gris italic, estilo WhatsApp).
  forwardedTag: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontStyle: "italic",
    color: "#8696a0",
    marginBottom: 3,
    "& svg": {
      fontSize: 13,
      transform: "scaleX(-1)",
    },
  },

  messagesList: {
    backgroundImage: theme.palette.type === "dark"
      // CSS-only subtle diamond pattern — no external asset needed in dark mode
      ? `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 1l9 9-9 9-9-9z' fill='none' stroke='rgba(255,255,255,0.04)' stroke-width='1'/%3E%3C/svg%3E")`
      : `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#0d1117" : "transparent",
    backgroundRepeat: "repeat",
    backgroundSize: theme.palette.type === "dark" ? "20px 20px" : "auto",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.action.hover,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#1a4731" : "#dcf8c6",
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#143d27" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.text.disabled,
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  // feat_361 — cartel de "mensaje no compatible". Mismo tratamiento visual que un
  // mensaje eliminado (gris, itálica): es contenido que existe pero no se muestra.
  unsupportedMessage: {
    fontStyle: "italic",
    color: theme.palette.text.disabled,
    overflowWrap: "break-word",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  // feat_271: cap de altura de media dentro del chat flotante (angosto).
  messageMediaCompact: {
    width: "100%",
    maxWidth: 250,
    height: "auto",
    maxHeight: 180,
    objectFit: "cover",
    cursor: "pointer",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.secondary,
  },

  timestampNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: theme.palette.text.secondary,
    position: "static",
    marginTop: 4,
    float: "right",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.background.paper,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackFailedIcon: {
    color: "#f44336",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
  messageAiBadge: {
    fontSize: 11,
    color: "#7c4dff",
    fontWeight: 600,
    display: "block",
    marginBottom: 2,
  },
  messageRightAI: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#1a1a4a" : "#e8eaff",
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
  },
  messageInternalNote: {
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 6,
    marginBottom: 2,
    minWidth: 100,
    maxWidth: 600,
    width: "fit-content",
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#2d2a1a" : "#FFF3CD",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "4px solid #f59e0b",
    color: theme.palette.type === "dark" ? "#ffe082" : "#856404",
    alignSelf: "center",
    borderRadius: 4,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 6,
    paddingBottom: 6,
    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
  },
  senderLabel: {
    fontSize: 11,
    fontWeight: 600,
    display: "block",
    marginBottom: 2,
    paddingLeft: 4,
  },
  senderLabelAdvisor: {
    color: "#2e7d32",
  },
  senderLabelAI: {
    color: "#5c6bc0",
  },
  senderLabelNote: {
    color: "#b45309",
    fontWeight: 700,
  },
  messageAiSummary: {
    backgroundColor: theme.palette.type === "dark" ? "#2d2a1a" : "#fffde7",
    border: "1px dashed #f59e0b",
    color: theme.palette.type === "dark" ? "#ffe082" : "#856404",
    alignSelf: "center",
    borderRadius: 8,
    margin: "4px 20px",
    padding: "6px 10px",
    fontSize: 13,
    maxWidth: "80%",
    fontStyle: "italic",
  },
  messageAiSummaryLabel: {
    fontSize: 11,
    fontWeight: 600,
    display: "block",
    marginBottom: 2,
    color: "#f59e0b",
  },
  scheduledBatch: {
    backgroundColor: theme.palette.type === "dark" ? "#1a2a1a" : "#f0fdf4",
    border: "1px dashed #22c55e",
    color: theme.palette.type === "dark" ? "#86efac" : "#15803d",
    alignSelf: "center",
    borderRadius: 8,
    margin: "4px 20px",
    padding: "6px 10px",
    fontSize: 13,
    maxWidth: "80%",
  },
  scheduledBatchHeader: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    color: "#22c55e",
    marginBottom: 4,
  },
  scheduledBatchCancelled: {
    opacity: 0.5,
    textDecoration: "line-through",
  },
  // Marcador cronológico de un envío programado terminal (cancelado / fallido).
  scheduledEvent: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2320" : "#fdf6f0",
    border: theme.palette.type === "dark" ? "1px solid #4b3b33" : "1px solid #e5d3c4",
    color: theme.palette.type === "dark" ? "#d6b8a6" : "#8a5a3c",
    alignSelf: "center",
    borderRadius: 8,
    margin: "4px 20px",
    padding: "6px 10px",
    fontSize: 13,
    maxWidth: "80%",
  },
  scheduledEventHeader: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 4,
  },
  scheduledEventBody: {
    fontSize: 12,
    opacity: 0.7,
    textDecoration: "line-through",
    padding: "1px 0",
  },
  scheduledEventTrace: {
    fontSize: 10,
    opacity: 0.6,
    marginTop: 4,
    fontStyle: "italic",
  },
  // Línea discreta de trazabilidad bajo un mensaje que se envió programado.
  scheduledTrace: {
    fontSize: 10,
    opacity: 0.6,
    fontStyle: "italic",
    display: "block",
    marginTop: 2,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Subtle per-participant background tones for group inbound bubbles.
// Only luminosity variation — no loud colors. Tone 0 = no change (base).
const GROUP_TONES_DARK = [
  "transparent",
  "rgba(255,255,255,0.03)",
  "rgba(255,255,255,0.06)",
  "rgba(0,0,0,0.08)",
  "rgba(255,255,255,0.09)",
];
const GROUP_TONES_LIGHT = [
  "transparent",
  "rgba(0,0,0,0.03)",
  "rgba(0,0,0,0.06)",
  "rgba(0,0,0,0.09)",
  "rgba(255,255,255,0.5)",
];
// Deterministic contactId -> tone index (sum of digits mod N).
const getToneIndex = (contactId, n) => {
  if (!contactId) return 0;
  return String(contactId)
    .split("")
    .reduce((acc, c) => acc + (Number(c) || 0), 0) % n;
};

const MessagesList = ({ ticketId, isGroup, readOnly, compact = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  // feat_315 — tz de la empresa para los timestamps. Si el backend no la expone
  // (perfil sin la key), queda undefined → el helper cae a la tz del navegador.
  const { getSetting } = useContext(SettingsContext);
  const timezone = getSetting("businessTimezone") || undefined;

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  // feat_346 — re-sync al despertar. Bumpear la clave hace RESET + página 1:
  // NUNCA re-fetchear sin RESET, porque LOAD_MESSAGES PREPENDEA (asume
  // paginación hacia atrás) y los mensajes que llegaron mientras dormía la
  // pestaña quedarían ARRIBA DE TODO, fuera de orden.
  const [resyncKey, setResyncKey] = useState(0);
  useResyncOnResume(() => setResyncKey(k => k + 1));
  const [copiedMsgId, setCopiedMsgId] = useState(null);

  // Scheduled batches
  const [scheduledBatches, setScheduledBatches] = useState([]);

  const fetchScheduledBatches = useCallback(async () => {
    if (!ticketId || readOnly) return;
    try {
      const { data } = await api.get(`/scheduled-messages?ticketId=${ticketId}`);
      setScheduledBatches(data);
    } catch (_) {}
  }, [ticketId, readOnly]);

  useEffect(() => {
    fetchScheduledBatches();
  }, [fetchScheduledBatches]);

  // Same-tab fallback when MessageInput just POSTed — fires before socket arrives
  useEffect(() => {
    if (!ticketId) return;
    const eventName = `scheduledMessagesRefresh:${ticketId}`;
    const handler = () => fetchScheduledBatches();
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [ticketId, fetchScheduledBatches]);

  const handleCancelBatch = async (batchId) => {
    try {
      await api.delete(`/scheduled-messages/batch/${batchId}`);
      fetchScheduledBatches();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyInternal = (msgId, text) => {
    navigator.clipboard.writeText(text || "");
    setCopiedMsgId(msgId);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId, resyncKey]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, ...(readOnly ? { readOnly: true } : {}) },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
    // feat_346 — `resyncKey` TAMBIÉN acá, no solo en el effect del RESET: si el
    // asesor no scrolleó historial, `pageNumber` ya vale 1 y `setPageNumber(1)`
    // no cambia ninguna dep → este fetch no se re-dispararía y el RESET dejaría
    // el chat VACÍO hasta cambiar de ticket. Misma trampa que en useTickets.
  }, [pageNumber, ticketId, resyncKey]);

  useEffect(() => {
    if (!ticketId || readOnly) return;

    const socket = getSocket();

    socket.emit("joinChatBox", ticketId.toString());

    const handleReconnect = () => {
      socket.emit("joinChatBox", ticketId.toString());
    };
    socket.on("connect", handleReconnect);

    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [ticketId, readOnly]);

  useEffect(() => {
    if (!ticketId) return;

    const socket = getSocket();
    const numericTicketId = Number(ticketId);

    const handleAppMessage = (data) => {
      if (Number(data.message?.ticketId) !== numericTicketId) return;

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
        // Refresh scheduled batches — a sent batch creates a real message, or
        // a contact reply may cancel pending batches (internal note arrives).
        fetchScheduledBatches();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };

    const handleScheduledMessage = (data) => {
      // Server emits without ticketId in cancel-on-reply; refresh when ticket matches or no filter
      if (data?.ticketId && Number(data.ticketId) !== numericTicketId) return;
      fetchScheduledBatches();
    };

    socket.on("appMessage", handleAppMessage);
    socket.on("scheduledMessage", handleScheduledMessage);

    return () => {
      socket.off("appMessage", handleAppMessage);
      socket.off("scheduledMessage", handleScheduledMessage);
    };
  }, [ticketId, fetchScheduledBatches]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const hasRealCaption = (message) => {
    if (!message.mediaType || message.mediaType === "chat") return true;
    // feat_361 — location y vcard tienen preview propio y su body es el dato crudo
    // (coordenadas, BEGIN:VCARD...), no un pie de foto: nunca se imprime tal cual.
    if (message.mediaType === "location" || message.mediaType === "vcard")
      return false;
    if (!message.body) return false;
    if (message.body === message.mediaUrl) return false;
    // R-0701 — el nombre del documento ya está en la tarjeta; no repetirlo como pie.
    if (isDocumentMediaType(message.mediaType) && looksLikeDocumentName(message.body))
      return false;
    return !isMediaFilename(message.body);
  };

  // feat_361 — red de seguridad contra la burbuja vacía. Un mensaje sin body, sin
  // adjunto y sin preview propio dibujaba solo la hora (co1 Del Metal, ticket 4037,
  // 2026-09-22): pasa con los tipos de Meta que el backend todavía no sabe parsear.
  // Decir "no compatible" es honesto; el globo mudo parece un mensaje perdido.
  // Media importada del historial de Coexistence: el backend no baja el binario
  // (mediaUrl null) y el globo no tiene qué dibujar. Se avisa en vez de mostrar
  // "no compatible", y se conserva el pie o el nombre del archivo si lo hay.
  const isHistoryMediaUnavailable = (message) =>
    message.senderType === "system" &&
    !message.mediaUrl &&
    HISTORY_MEDIA_TYPES.includes(message.mediaType);

  const renderBody = (message) => {
    if (isHistoryMediaUnavailable(message)) {
      const caption = message.body && message.body.trim();
      return (
        <>
          <div className={classes.unsupportedMessage}>
            {i18n.t("messagesList.historyMediaUnavailable")}
          </div>
          {caption && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
        </>
      );
    }
    const hasOwnPreview =
      message.mediaUrl ||
      message.mediaType === "location" ||
      message.mediaType === "vcard";
    const bodyIsEmpty = !message.body || !message.body.trim();

    if (bodyIsEmpty && !hasOwnPreview && !message.isDeleted) {
      return (
        <span className={classes.unsupportedMessage}>
          {i18n.t("messagesList.unsupportedMessage")}
        </span>
      );
    }
    if (!hasRealCaption(message)) return null;
    return <MarkdownWrapper>{message.body}</MarkdownWrapper>;
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if ( /^.*\.(jpe?g|png|gif|webp)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} compact={compact} />;
    } else if (message.mediaType === "audio") {
      return (
        <>
          <Audio url={message.mediaUrl} />
          <AudioTranscription message={message} />
        </>
      );
    } else if (message.mediaType === "video") {
      return (
        <video
          className={compact ? classes.messageMediaCompact : classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return <DocumentPreview message={message} />;
    }
  };

  const renderMessageAck = (message) => {
    if (message.deliveryFailed) {
      return (
        <MuiTooltip title={getMetaErrorMessage(message.deliveryErrorCode)}>
          <ErrorOutline fontSize="small" className={classes.ackFailedIcon} />
        </MuiTooltip>
      );
    }
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    // feat_315 (fix B-1) — el cambio de día se evalúa para CUALQUIER índice, incluido
    // el último. Antes la rama del separador estaba gateada con
    // `index < messagesList.length - 1` y el último mensaje caía directo a la rama del
    // ref de scroll → si el último mensaje del hilo abría un día nuevo, no se dibujaba
    // el cartelito (caso frecuente: el primer mensaje de hoy en un chat abierto).
    const messageDay = parseISO(messagesList[index].createdAt);
    const previousMessageDay = parseISO(messagesList[index - 1].createdAt);
    const dayChanged = !isSameDay(messageDay, previousMessageDay);

    const daySeparator = (
      <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
        <div className={classes.dailyTimestampText}>
          {format(messageDay, "dd/MM/yyyy")}
        </div>
      </span>
    );

    if (index === messagesList.length - 1) {
      // Último mensaje: separador si cambia el día + el ref de scroll SIEMPRE.
      return (
        <React.Fragment key={`last-${message.id}`}>
          {dayChanged && daySeparator}
          <div
            key={`ref-${message.createdAt}`}
            ref={lastMessageRef}
            style={{ float: "left", clear: "both" }}
          />
        </React.Fragment>
      );
    }

    if (dayChanged) {
      return daySeparator;
    }

    return null;
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const hasRealQuotedCaption = (qm) => {
    if (!qm) return false;
    const mt = qm.mediaType;
    if (!mt || mt === "chat") return !!qm.body;
    if (!qm.body) return false;
    if (qm.body === qm.mediaUrl) return false;
    if (isDocumentMediaType(mt) && looksLikeDocumentName(qm.body)) return false;
    return !isMediaFilename(qm.body);
  };

  const getQuotedMediaInfo = (qm) => {
    const mt = qm?.mediaType;
    const realCaption = hasRealQuotedCaption(qm);
    switch (mt) {
      case "sticker": return { icon: "🎴", text: i18n.t("quotedMessage.sticker") };
      case "image": return { icon: "📷", text: realCaption ? qm.body : i18n.t("quotedMessage.image") };
      case "video": return { icon: "🎬", text: realCaption ? qm.body : i18n.t("quotedMessage.video") };
      case "audio":
      case "ptt": return { icon: "🎤", text: i18n.t("quotedMessage.audio") };
      // R-0701 — "file"/"application" también son documentos (salientes por WAIA /
      // QR por mimetype). Sin pie real se muestra el nombre del archivo con el
      // ícono de su extensión, como en WhatsApp.
      case "document":
      case "file":
      case "application": {
        const ext = getDocumentExtension(qm.mediaUrl) || getDocumentExtension(qm.body);
        return {
          icon: getDocumentIcon(ext),
          text: realCaption
            ? qm.body
            : parseDocumentName(qm.body) || i18n.t("quotedMessage.document"),
        };
      }
      case "vcard": return { icon: "👤", text: i18n.t("quotedMessage.vcard") };
      case "location": return { icon: "📍", text: i18n.t("quotedMessage.location") };
      default: return null;
    }
  };

  // feat_264 — indicador "↪ Reenviado" / "↪ Reenviado muchas veces" (score ≥ 4).
  const renderForwarded = (message) => {
    if (!message.isForwarded) return null;
    return (
      <span className={classes.forwardedTag}>
        <ReplyIcon fontSize="inherit" />
        {message.forwardingScore >= 4
          ? i18n.t("messagesList.forwardedMany")
          : i18n.t("messagesList.forwarded")}
      </span>
    );
  };

  const renderQuotedMessage = (message) => {
    const qm = message.quotedMsg;
    const mediaInfo = qm && qm.mediaType && qm.mediaType !== "chat"
      ? getQuotedMediaInfo(qm)
      : null;
    const showThumbnail = mediaInfo && (qm.mediaType === "sticker" || qm.mediaType === "image") && qm.mediaUrl;
    // When there's a visible thumbnail and no real caption, hide secondary text (WhatsApp Web parity)
    const showSecondaryText = !showThumbnail || hasRealQuotedCaption(qm);

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: qm?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!qm?.fromMe && (
            <span
              className={classes.messageContactName}
              style={{ color: getParticipantColor(qm?.contact?.id) }}
            >
              {qm?.contact?.name || qm?.contact?.whatsappName}
            </span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {showSecondaryText && (
              <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {mediaInfo ? (
                  <span>{mediaInfo.icon} {mediaInfo.text}</span>
                ) : (
                  qm?.body
                )}
              </div>
            )}
            {showThumbnail && (
              <img
                src={qm.mediaUrl}
                alt=""
                style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                onError={e => { e.target.style.display = "none"; }}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Línea discreta "Programado por X · para <fecha>" — reusada por la burbuja
  // enviada (trazabilidad) y por el marcador cronológico de estado terminal.
  const buildScheduleTrace = (by, forDate) => {
    const parts = [];
    parts.push(
      by
        ? i18n.t("scheduledMessages.trace_by", { name: by })
        : i18n.t("scheduledMessages.trace_anon")
    );
    if (forDate) {
      parts.push(
        i18n.t("scheduledMessages.trace_for", {
          datetime: fmtDateTimeShort(new Date(forDate)),
        })
      );
    }
    return parts.join(" · ");
  };

  // Título del marcador terminal según evento + motivo.
  const scheduledEventTitle = (ev) => {
    if (ev.event === "failed") {
      const total = ev.total || (ev.bodies ? ev.bodies.length : 0);
      if (ev.sentCount > 0 && ev.sentCount < total) {
        return i18n.t("scheduledMessages.event_failed_partial", {
          sent: ev.sentCount,
          total,
        });
      }
      if (ev.reason === "window_expired") {
        return i18n.t("scheduledMessages.event_failed_window");
      }
      return i18n.t("scheduledMessages.event_failed");
    }
    // cancelled
    switch (ev.reason) {
      case "contact_replied":
        return i18n.t("scheduledMessages.event_cancelled_reply");
      case "ticket_closed":
        return i18n.t("scheduledMessages.event_cancelled_closed");
      case "ticket_not_found":
        return i18n.t("scheduledMessages.event_cancelled_notfound");
      case "manual":
      default:
        return i18n.t("scheduledMessages.event_cancelled_manual");
    }
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        // AI summary messages render as centered note
        if (message.isAiSummary) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div className={classes.messageAiSummary}>
                <span className={classes.messageAiSummaryLabel}>📋 Resumen IA</span>
                <MarkdownWrapper>{message.body}</MarkdownWrapper>
              </div>
            </React.Fragment>
          );
        }

        // MatchProp alert: rich card parsed from the MATCHPROP_V1: payload.
        // Falls through to plain text if the prefix is missing or unparsable.
        if (message.senderType === "matchprop-alert") {
          const idx = message.body ? message.body.indexOf("MATCHPROP_V1:") : -1;
          if (idx !== -1) {
            let mpPayload = null;
            try {
              mpPayload = JSON.parse(message.body.slice(idx + "MATCHPROP_V1:".length));
            } catch (e) {
              mpPayload = null;
            }
            if (mpPayload) {
              return (
                <React.Fragment key={message.id}>
                  {renderDailyTimestamps(message, index)}
                  <MatchPropAlertCard payload={mpPayload} createdAt={message.createdAt} />
                </React.Fragment>
              );
            }
          }
        }

        // Scheduled-event marker: lote programado terminal (cancelado / fallido)
        // en su lugar cronológico. Reemplaza la tarjeta fija al pie.
        if (message.senderType === "scheduled-event") {
          const sIdx = message.body ? message.body.indexOf("SCHEDEVENT_V1:") : -1;
          let ev = null;
          if (sIdx !== -1) {
            try {
              ev = JSON.parse(message.body.slice(sIdx + "SCHEDEVENT_V1:".length));
            } catch (e) {
              ev = null;
            }
          }
          if (ev) {
            const trace = buildScheduleTrace(ev.scheduledBy, ev.scheduledFor);
            return (
              <React.Fragment key={message.id}>
                {renderDailyTimestamps(message, index)}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div className={classes.scheduledEvent}>
                    <div className={classes.scheduledEventHeader}>
                      <ScheduleIcon style={{ fontSize: 13 }} />
                      {scheduledEventTitle(ev)}
                    </div>
                    {(ev.bodies || []).map((b, i2) => (
                      <div key={i2} className={classes.scheduledEventBody}>
                        {b}
                      </div>
                    ))}
                    {trace && (
                      <div className={classes.scheduledEventTrace}>{trace}</div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          }
        }

        // Internal note: centered, yellow border-left — copy icon lives next to the timestamp
        if (message.senderType === "internal-note") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className={classes.messageInternalNote}>
                  <span className={clsx(classes.senderLabel, classes.senderLabelNote)}>📋 Nota interna</span>
                  <div className={classes.textContentItem}>
                    <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  </div>
                  <span className={classes.timestampNote}>
                    {fmtSmartTimestamp(message.createdAt, { timezone })}
                    <Tooltip title={copiedMsgId === message.id ? "¡Copiado!" : "Copiar"} placement="top">
                      <span
                        onClick={() => handleCopyInternal(message.id, message.body)}
                        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", fontSize: 14 }}
                      >
                        {copiedMsgId === message.id
                          ? <Done style={{ fontSize: 14, color: "green" }} />
                          : <FileCopy style={{ fontSize: 14 }} />}
                      </span>
                    </Tooltip>
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        // Hide repeated sender label across consecutive messages from the
        // same sender (WhatsApp-style). Only for normal messages — special
        // renders (internal-note, matchprop-alert, AI summary) return earlier.
        const senderKey = (m) => {
          if (!m || m.isAiSummary) return null;
          if (m.senderType === "internal-note" || m.senderType === "matchprop-alert" || m.senderType === "scheduled-event") return null;
          if (!m.fromMe) return `c:${m.contactId ?? m.contact?.id ?? "?"}`;
          if (m.senderType === "ai" || m.fromAI) return "ai";
          // Outbound (asesor): distinguir por userId; sin userId -> "me"
          // (todos mis mensajes son la misma racha, incl. grupos).
          return m.userId ? `u:${m.userId}` : "me";
        };
        const prevMessage = messagesList[index - 1];
        const prevKey = senderKey(prevMessage);
        const showSenderLabel =
          index === 0 || prevKey === null || senderKey(message) !== prevKey;

        if (!message.fromMe) {
          // Subtle per-participant tone, groups only (1-a-1 sin cambio).
          const tones = isDark ? GROUP_TONES_DARK : GROUP_TONES_LIGHT;
          const tone = isGroup ? tones[getToneIndex(message.contactId, tones.length)] : "transparent";
          // tono 0 = "transparent" -> NO setear. Para el resto usar gradient
          // como overlay para NO pisar el backgroundColor base (paper) de la
          // burbuja: la clase pinta el paper, el gradient tinta encima.
          const toneStyle =
            tone !== "transparent"
              ? { backgroundImage: `linear-gradient(${tone}, ${tone})` }
              : {};
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageLeft} style={toneStyle}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && showSenderLabel && (
                  <span
                    className={classes.messageContactName}
                    style={{ color: getParticipantColor(message.contact?.id) }}
                  >
                    {message.contact?.name || message.contact?.whatsappName}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard"
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {renderForwarded(message)}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {renderBody(message)}
                  <span className={classes.timestamp}>
                    {fmtSmartTimestamp(message.createdAt, { timezone })}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          const isAI = message.senderType === "ai" || message.fromAI;
          const bubbleClass = isAI ? classes.messageRightAI : classes.messageRight;

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={bubbleClass}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && !message.fromMe && message.user?.name && showSenderLabel && (
                  <span className={classes.messageContactName}>
                    {message.user.name}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {isAI && !message.isAiSummary && showSenderLabel && (
                    <span className={clsx(classes.senderLabel, classes.senderLabelAI)}>🤖 IA</span>
                  )}
                  {!isAI && message.senderType === "advisor" && message.user?.name && showSenderLabel && (
                    <span className={clsx(classes.senderLabel, classes.senderLabelAdvisor)}>👤 {message.user.name}</span>
                  )}
                  {message.senderType === "turnos-advisor" && showSenderLabel && (
                    <span
                      className={clsx(classes.senderLabel, classes.senderLabelAdvisor)}
                      style={{ background: "#00695c", color: "#fff" }}
                    >
                      🗓 {i18n.t("turnosChat.badgeActive")}
                      {message.user?.name ? ` · ${message.user.name}` : ""}
                    </span>
                  )}
                  {renderForwarded(message)}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {renderBody(message)}
                  <span className={classes.timestamp}>
                    {fmtSmartTimestamp(message.createdAt, { timezone })}
                    {message.fromScheduled && (
                      <MuiTooltip
                        title={
                          message.scheduledInfo
                            ? buildScheduleTrace(
                                message.scheduledInfo.scheduledBy,
                                message.scheduledInfo.scheduledFor
                              )
                            : i18n.t("scheduledMessages.was_scheduled")
                        }
                      >
                        <ScheduleIcon style={{ fontSize: 12, marginLeft: 3, opacity: 0.7, verticalAlign: "middle" }} />
                      </MuiTooltip>
                    )}
                    {renderMessageAck(message)}
                  </span>
                  {message.fromScheduled && message.scheduledInfo && (
                    <span className={classes.scheduledTrace}>
                      {buildScheduleTrace(
                        message.scheduledInfo.scheduledBy,
                        message.scheduledInfo.scheduledFor
                      )}
                    </span>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        {/* Solo lotes VIGENTES (pending/sending) quedan fijos al pie: son eventos
            futuros y accionables. Los estados terminales (cancelado/fallado) ahora
            se renderizan como marcador cronológico (senderType="scheduled-event")
            en el hilo, no acá. */}
        {scheduledBatches
          .filter(b => b.status === "pending" || b.status === "sending")
          .sort((a, b) => {
            const av = Date.parse(a.scheduledFor || a.messages?.[0]?.scheduledFor || "");
            const bv = Date.parse(b.scheduledFor || b.messages?.[0]?.scheduledFor || "");
            return (Number.isNaN(av) ? Infinity : av) - (Number.isNaN(bv) ? Infinity : bv);
          })
          .map(batch => (
          <div key={batch.batchId} style={{ display: "flex", justifyContent: "center" }}>
            <div className={classes.scheduledBatch}>
              <div className={classes.scheduledBatchHeader}>
                <ScheduleIcon style={{ fontSize: 13 }} />
                {batch.status === "sending"
                  ? i18n.t("scheduledMessages.sending")
                  : i18n.t("scheduledMessages.badge", { datetime: fmtDateTimeShort(new Date(batch.scheduledFor)) })}
                {batch.status === "pending" && !readOnly && (
                  <MuiTooltip title={i18n.t("scheduledMessages.cancel_action")}>
                    <span
                      onClick={() => handleCancelBatch(batch.batchId)}
                      style={{ cursor: "pointer", marginLeft: 4, display: "inline-flex" }}
                    >
                      <CancelIcon style={{ fontSize: 13, color: "#ef4444" }} />
                    </span>
                  </MuiTooltip>
                )}
              </div>
              {batch.messages.map((m, idx) => (
                <div key={m.id} style={{ fontSize: 12, padding: "2px 0", borderTop: idx > 0 ? "1px solid rgba(34,197,94,0.2)" : "none", marginTop: idx > 0 ? 3 : 0 }}>
                  {m.body}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;